import { SbomToolSchema, type SbomTool, type Tool } from './api-client/api-schemas'

export type ScaTool = Extract<Tool, SbomTool>

/**
 * DAST (Dynamic Application Security Testing) tools scan a running application and
 * report findings by attacked URL rather than by source file. OWASP ZAP is the first.
 */
export const DAST_TOOLS = ['zap'] as const satisfies readonly Tool[]
export type DastTool = (typeof DAST_TOOLS)[number]

export type SastTool = Exclude<Tool, ScaTool | DastTool>

/**
 * Determines if the given tool supports SBOM (Software Bill of Materials) uploads.
 * SBOM tools can have additional archive files uploaded alongside scan results.
 *
 * @param tool - The tool to check
 * @returns true if the tool supports SBOM uploads, false otherwise
 */
export function isSbomTool(tool: Tool): tool is ScaTool {
  const { success } = SbomToolSchema.safeParse(tool)

  return success
}

/**
 * Determines if the given tool is an SCA (Software Composition Analysis) tool.
 * SCA tools analyze dependencies and third-party libraries for security vulnerabilities.
 *
 * @param tool - The tool to check
 * @returns true if the tool is an SCA tool, false otherwise
 */
export function isScaTool(tool: Tool): tool is ScaTool {
  return isSbomTool(tool)
}

/**
 * Determines if the given tool is a DAST (Dynamic Application Security Testing) tool.
 * DAST tools scan a running application and locate findings by attacked URL/endpoint
 * rather than by source file.
 *
 * @param tool - The tool to check
 * @returns true if the tool is a DAST tool, false otherwise
 */
export function isDastTool(tool: Tool): tool is DastTool {
  return (DAST_TOOLS as readonly Tool[]).includes(tool)
}

/**
 * Determines if the given tool is a SAST (Static Application Security Testing) tool.
 * SAST tools analyze source code for security vulnerabilities and code quality issues.
 *
 * @param tool - The tool to check
 * @returns true if the tool is a SAST tool, false otherwise
 */
export function isSastTool(tool: Tool): tool is SastTool {
  return !isScaTool(tool) && !isDastTool(tool)
}
